import { useState } from "react";

/* ============================================================
   DESIGN TOKENS
   ============================================================ */
const T = {
  primary:   "#123133",
  accent:    "#10c9a5",
  accentHov: "#0eb894",
  white:     "#ffffff",
  border:    "rgba(18,49,51,0.12)",
  borderMd:  "rgba(18,49,51,0.22)",
  text:      "#123133",
  textMuted: "rgba(18,49,51,0.5)",
  textHint:  "rgba(18,49,51,0.35)",
  danger:    "#c0392b",
};

const API = (window._ADMIN_API_URL || process.env?.REACT_APP_API_URL || "/api/v1");

/* ============================================================
   LOGIN STYLES
   ============================================================ */
const LoginStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body, #root { height: 100%; }

    .adm-eyebrow {
      font-size: 11px; letter-spacing: 0.18em;
      text-transform: uppercase; color: ${T.accent};
      margin-bottom: 8px;
    }

    .adm-form { display: flex; flex-direction: column; gap: 16px; }
    .adm-field { display: flex; flex-direction: column; gap: 6px; }
    .adm-label {
      font-size: 11px; letter-spacing: 0.14em;
      text-transform: uppercase; color: ${T.textMuted};
    }
    .adm-input {
      padding: 9px 12px;
      border: 1px solid ${T.border};
      background: ${T.white};
      color: ${T.text};
      font-size: 13px; font-family: inherit;
      outline: none;
      transition: border-color .15s;
    }
    .adm-input:focus { border-color: ${T.accent}; }

    .adm-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 7px;
      padding: 10px 20px;
      font-size: 12px; letter-spacing: 0.08em;
      font-family: inherit;
      cursor: pointer; border: 1px solid ${T.borderMd};
      background: transparent; color: ${T.text};
      transition: all .15s; white-space: nowrap;
    }
    .adm-btn--primary {
      background: ${T.accent}; color: ${T.primary};
      border-color: ${T.accent}; font-weight: 500;
    }
    .adm-btn--primary:hover { background: ${T.accentHov}; border-color: ${T.accentHov}; }
    .adm-btn:disabled { opacity: 0.45; cursor: not-allowed; }

    .adm-spinner {
      display: inline-block; width: 18px; height: 18px;
      border: 2px solid rgba(18,49,51,0.15);
      border-top-color: ${T.accent};
      border-radius: 50%;
      animation: spin .6s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
  `}</style>
);

/* ============================================================
   ICONS
   ============================================================ */
const ArrowIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const ShieldIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

/* ============================================================
   LOGIN SCREEN
   ============================================================ */
export default function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempEmail, setTempEmail] = useState("");
  const [tempPassword, setTempPassword] = useState("");

  // Первый шаг: проверка пароля
  const submitPassword = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.status === 202) {
        // Требуется 2FA
        setRequires2FA(true);
        setTempEmail(email);
        setTempPassword(password);
        setError(null);
        setTwoFACode("");
      } else if (res.ok) {
        const data = await res.json();
        // Проверяем роль
        try {
          const payload = JSON.parse(atob(data.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
          if (payload?.role !== "admin") {
            throw new Error("Доступ запрещён: требуется роль администратора");
          }
        } catch (decodeErr) {
          if (decodeErr.message.includes("Доступ")) throw decodeErr;
          throw new Error("Не удалось прочитать токен");
        }
        onLogin(data);
      } else {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Неверный email или пароль");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Второй шаг: проверка 2FA кода
  const submit2FA = async () => {
    if (!twoFACode || twoFACode.length < 6) {
      setError("Введите 6-значный код из приложения");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/login/2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: tempEmail,
          password: tempPassword,
          code: twoFACode,
        }),
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Неверный код подтверждения");
      }

      const data = await res.json();
      // Проверяем роль
      try {
        const payload = JSON.parse(atob(data.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (payload?.role !== "admin") {
          throw new Error("Доступ запрещён: требуется роль администратора");
        }
      } catch (decodeErr) {
        if (decodeErr.message.includes("Доступ")) throw decodeErr;
        throw new Error("Не удалось прочитать токен");
      }
      onLogin(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Обработка нажатия Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (requires2FA) {
        submit2FA();
      } else {
        submitPassword();
      }
    }
  };

  // Сброс 2FA (вернуться к форме входа)
  const backToLogin = () => {
    setRequires2FA(false);
    setTempEmail("");
    setTempPassword("");
    setTwoFACode("");
    setError(null);
  };

  // Форма ввода пароля
  if (!requires2FA) {
    return (
      <>
        <LoginStyle />
        <div style={{
          minHeight: "100vh",
          background: T.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div style={{ width: 360, background: T.white, padding: "40px 36px" }}>
            <div className="adm-eyebrow" style={{ marginBottom: 4 }}>ITS</div>
            <div style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: 28, fontWeight: 400,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: T.primary,
              marginBottom: 28,
            }}>
              Панель<br />
              <span style={{ color: T.accent }}>авторизации</span>
            </div>

            <div className="adm-form">
              <div className="adm-field">
                <label className="adm-label">Email</label>
                <input
                  className="adm-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
              </div>
              <div className="adm-field">
                <label className="adm-label">Пароль</label>
                <input
                  className="adm-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              {error && (
                <div style={{ fontSize: 13, color: T.danger }}>{error}</div>
              )}
              <button
                className="adm-btn adm-btn--primary"
                onClick={submitPassword}
                disabled={loading}
                style={{ marginTop: 8 }}
              >
                {loading ? <span className="adm-spinner" /> : ArrowIcon}
                {loading ? "Вход..." : "Войти"}
              </button>
            </div>

            <div style={{ marginTop: 24, fontSize: 11, color: T.textHint }}>
              Доступ только для суперпользователей
            </div>
          </div>
        </div>
      </>
    );
  }

  // Форма ввода 2FA кода
  return (
    <>
      <LoginStyle />
      <div style={{
        minHeight: "100vh",
        background: T.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <div style={{ width: 360, background: T.white, padding: "40px 36px" }}>
          <div className="adm-eyebrow" style={{ marginBottom: 4 }}>ITS</div>
          <div style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: 28, fontWeight: 400,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: T.primary,
            marginBottom: 28,
          }}>
            Двухфакторная<br />
            <span style={{ color: T.accent }}>аутентификация</span>
          </div>

          <div className="adm-form">
            <div className="adm-field">
              <label className="adm-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {ShieldIcon}
                Код из Google Authenticator
              </label>
              <input
                className="adm-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={twoFACode}
                onChange={e => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{ textAlign: "center", fontSize: 18, letterSpacing: 4 }}
              />
              <div style={{ fontSize: 11, color: T.textHint, marginTop: 4 }}>
                Введите 6-значный код из приложения-аутентификатора
              </div>
            </div>

            {error && (
              <div style={{ fontSize: 13, color: T.danger }}>{error}</div>
            )}

            <button
              className="adm-btn adm-btn--primary"
              onClick={submit2FA}
              disabled={loading || twoFACode.length !== 6}
              style={{ marginTop: 8 }}
            >
              {loading ? <span className="adm-spinner" /> : ShieldIcon}
              {loading ? "Проверка..." : "Подтвердить"}
            </button>

            <button
              className="adm-btn"
              onClick={backToLogin}
              disabled={loading}
              style={{ marginTop: 8 }}
            >
              ← Назад
            </button>
          </div>

          <div style={{ marginTop: 24, fontSize: 11, color: T.textHint, textAlign: "center" }}>
            Откройте Google Authenticator и введите код
          </div>
        </div>
      </div>
    </>
  );
}